import { CATALOG_ITEM_STATUSES, CIRCULATION_OVERRIDES } from "@loot-os/api-contract";
import type { CatalogItemStatus, Circulation } from "@loot-os/core/db/schema";
import { taxonomyFromBoxedTags } from "@loot-os/fulfillment-core/catalog/from-boxed";
import type { AvailabilityStrategy, PricingSource } from "@loot-os/fulfillment-core/catalog/item";
import {
  printingsForCardGame,
  TCG_CARD_GAMES,
  type TcgCardGame,
} from "@loot-os/fulfillment-core/catalog/tcg-import";
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferInput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

/** What `platform.fulfillment.items.create` accepts. The form sends a kind's subset. */
export type CreateCatalogItemInput = inferInput<
  TRPCOptionsProxy<AppRouter>["platform"]["fulfillment"]["items"]["create"]
>;

export { CATALOG_ITEM_STATUSES, CIRCULATION_OVERRIDES };

/** Item status, the same members as `CatalogItemStatus`. */
export type CatalogItemStatusChoice = (typeof CATALOG_ITEM_STATUSES)[number];

/** Circulation override, the same members as `Circulation`. Unset leaves the policy value. */
export type CirculationOverride = (typeof CIRCULATION_OVERRIDES)[number];

/** The kind the create form starts from. It decides which fields are asked and what is derived. */
export const CATALOG_ITEM_KINDS = ["SINGLE", "SLABBED", "SEALED", "OTHER"] as const;
export type CatalogItemKind = (typeof CATALOG_ITEM_KINDS)[number];

export const CATALOG_ITEM_KIND_LABELS: Record<CatalogItemKind, string> = {
  SINGLE: "Single",
  SLABBED: "Slabbed",
  SEALED: "Sealed",
  OTHER: "Other",
};

const COMPARE_AT_LABEL_MAX = 40;

/**
 * The create form, one string or boolean per input. Money is dollars until submit.
 * Tags, category, type, pricing ref and attributes are not inputs: the kind derives them.
 */
export interface CatalogItemCreateDraft {
  kind: CatalogItemKind;
  name: string;
  sourceId: string;
  cardGame: string;
  printing: string;
  collectionName: string;
  image: string;
  valueDollars: string;
  marketPriceDollars: string;
  compareAtValueDollars: string;
  compareAtLabelOverride: string;
  pricingSource: PricingSource;
  availabilityStrategy: AvailabilityStrategy;
  status: CatalogItemStatusChoice;
  overrideCirculation: CirculationOverride | "";
  alreadyProcured: boolean;
  quantity: string;
  tenantId: string;
}

/** A blank form for that kind, with its pricing, availability and status defaults. */
export function draftForKind(kind: CatalogItemKind): CatalogItemCreateDraft {
  const manual = kind !== "SINGLE";
  return {
    kind,
    name: "",
    sourceId: "",
    cardGame: "",
    printing: "",
    collectionName: "",
    image: "",
    valueDollars: "",
    marketPriceDollars: "",
    compareAtValueDollars: "",
    compareAtLabelOverride: "",
    pricingSource: manual ? "MANUAL" : "TCGPLAYER",
    availabilityStrategy: manual ? "FIXED" : "DYNAMIC",
    status: manual ? "STANDARD" : "PRE_RELEASE",
    overrideCirculation: "",
    alreadyProcured: false,
    quantity: "",
    tenantId: "",
  };
}

export function emptyCatalogItemDraft(): CatalogItemCreateDraft {
  return draftForKind("SINGLE");
}

export type CatalogItemFieldErrors = Partial<Record<keyof CatalogItemCreateDraft, string>>;

export type CatalogItemCreateParse =
  | { ok: true; input: CreateCatalogItemInput }
  | { ok: false; errors: CatalogItemFieldErrors };

/** Turn the form into the create input, or the field errors that block it. */
export function toCreateCatalogItemInput(draft: CatalogItemCreateDraft): CatalogItemCreateParse {
  const errors: CatalogItemFieldErrors = {};
  const name = draft.name.trim();
  if (!name) errors.name = "Full name is required.";

  const game = tcgCardGame(draft.cardGame);
  if (!draft.cardGame.trim()) errors.cardGame = "Card game is required.";
  else if (!game) errors.cardGame = "Choose a card game.";

  const printing = draft.printing.trim();
  if (game && printing && !printingsForCardGame(game).includes(printing)) {
    errors.printing = "Choose a printing for this game.";
  }

  const quantity = onHandQuantity(draft, errors);
  const manual = draft.kind !== "SINGLE";
  const marketPrice = manual
    ? requireDollars(draft.marketPriceDollars, "marketPriceDollars", errors, "Market price")
    : null;
  const value =
    manual && marketPrice !== null
      ? requireDollars(draft.valueDollars, "valueDollars", errors, "Value")
      : null;
  const collectionName = draft.collectionName.trim();
  if (manual && !collectionName) errors.collectionName = "Collection name is required.";

  if (!manual) {
    if (!draft.sourceId.trim()) errors.sourceId = "Source id is required.";
    if (!printing && errors.printing === undefined) errors.printing = "Card printing is required.";
  }

  const compared = readCompareAt(draft, value, errors);
  if (Object.keys(errors).length > 0 || !game || !compared) return { ok: false, errors };

  const taxonomy = taxonomyFromBoxedTags([game]);
  const sourceId = draft.sourceId.trim();
  const base: CreateCatalogItemInput = manual
    ? {
        name,
        image: blankToNull(draft.image),
        sourceId: null,
        value,
        marketPrice,
        pricingSource: draft.pricingSource,
        pricingRef: null,
        availabilityStrategy: draft.availabilityStrategy,
        status: itemStatus(draft.status),
        category: taxonomy.category,
        subcategory: taxonomy.subcategory,
        type: draft.kind,
        tags: [game],
        attributes: {
          boxedProtocol: "CUSTOM_TCG",
          collectionName,
          ...(printing ? { printing } : {}),
        },
      }
    : {
        name,
        sourceId,
        pricingSource: draft.pricingSource,
        pricingRef: { productId: sourceId, printing },
        availabilityStrategy: draft.availabilityStrategy,
        status: itemStatus(draft.status),
        category: taxonomy.category,
        subcategory: taxonomy.subcategory,
        type: "SINGLE",
        tags: [game],
        attributes: {
          boxedProtocol: "TCG",
          filter: { productId: sourceId, printing },
        },
      };

  return { ok: true, input: withOptions(base, draft, compared, quantity) };
}

export function isMember<T extends string>(values: readonly T[], value: string | null): value is T {
  return value !== null && (values as readonly string[]).includes(value);
}

/** The form's names are the schema enum's values; catalog-item.test.ts keeps the lists equal. */
function itemStatus(status: CatalogItemStatusChoice): CatalogItemStatus {
  return status as CatalogItemStatus;
}

function circulationOf(value: CirculationOverride): Circulation {
  return value as Circulation;
}

function tcgCardGame(value: string): TcgCardGame | null {
  const code = value.trim().toUpperCase();
  return (TCG_CARD_GAMES as readonly string[]).includes(code) ? (code as TcgCardGame) : null;
}

function withOptions(
  input: CreateCatalogItemInput,
  draft: CatalogItemCreateDraft,
  compared: { compareAtValue: number | null; label: string },
  quantity: number | null,
): CreateCatalogItemInput {
  const next = { ...input };
  if (compared.compareAtValue !== null) {
    next.compareAtValue = compared.compareAtValue;
    next.compareAtLabelOverride = compared.label || null;
  }
  if (draft.overrideCirculation)
    next.overrideCirculation = circulationOf(draft.overrideCirculation);
  const tenantId = blankToNull(draft.tenantId);
  if (tenantId) next.tenantId = tenantId;
  if (quantity !== null) {
    next.quantity = quantity;
    next.alreadyProcured = true;
  }
  return next;
}

function readCompareAt(
  draft: CatalogItemCreateDraft,
  value: number | null,
  errors: CatalogItemFieldErrors,
): { compareAtValue: number | null; label: string } | null {
  const compareAtValue = readDollars(draft.compareAtValueDollars, "compareAtValueDollars", errors);
  const label = draft.compareAtLabelOverride.trim();
  if (label && compareAtValue === null && errors.compareAtValueDollars === undefined) {
    errors.compareAtLabelOverride = "A compare-at label needs a compare-at value.";
  } else if (label.length > COMPARE_AT_LABEL_MAX) {
    errors.compareAtLabelOverride = "Compare-at label must be 40 characters or fewer.";
  }
  if (
    compareAtValue !== null &&
    errors.compareAtValueDollars === undefined &&
    errors.valueDollars === undefined &&
    compareAtValue <= (value ?? 0)
  ) {
    errors.compareAtValueDollars = "Compare-at value must be greater than value.";
  }
  return { compareAtValue, label };
}

/** Units already in the facility. Off sends nothing; on requires 1 or more. */
function onHandQuantity(
  draft: CatalogItemCreateDraft,
  errors: CatalogItemFieldErrors,
): number | null {
  if (!draft.alreadyProcured) return null;
  const parsed = wholeNumber(draft.quantity);
  if (parsed === null || parsed === "invalid" || parsed < 1) {
    errors.quantity = "Enter a whole number of 1 or more.";
    return null;
  }
  return parsed;
}

function blankToNull(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Value suggested from a market price: 1.25 times that price, rounded to the nearest cent.
 * Null when the market price is blank or not a dollar amount.
 */
export function suggestedValueDollars(marketPriceDollars: string): string | null {
  const cents = dollarsToCents(marketPriceDollars);
  if (cents === null || cents === "invalid") return null;
  const suggested = Math.round((cents * 5) / 4);
  const whole = Math.floor(suggested / 100);
  const fraction = String(suggested % 100).padStart(2, "0");
  return `${whole}.${fraction}`;
}

/** Dollars as typed, to integer cents. Blank is null; anything else unreadable is invalid. */
function dollarsToCents(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  const whole = match?.[1];
  if (!match || whole === undefined) return "invalid";
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return Number(whole) * 100 + Number(fraction);
}

function wholeNumber(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return "invalid";
  return Number(trimmed);
}

function readDollars(
  raw: string,
  key: keyof CatalogItemCreateDraft,
  errors: CatalogItemFieldErrors,
): number | null {
  const parsed = dollarsToCents(raw);
  if (parsed === "invalid") {
    errors[key] = "Enter a dollar amount, like 12.50.";
    return null;
  }
  return parsed;
}

function requireDollars(
  raw: string,
  key: keyof CatalogItemCreateDraft,
  errors: CatalogItemFieldErrors,
  label: string,
): number | null {
  const parsed = readDollars(raw, key, errors);
  if (parsed === null && errors[key] === undefined) errors[key] = `${label} is required.`;
  return parsed;
}
